import {
  Component,
  OnInit,
  OnDestroy,
  OnChanges,
  Input,
  Output,
  EventEmitter,
  SimpleChanges,
  ChangeDetectorRef,
  HostListener,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { of } from 'rxjs';
import { catchError, filter, tap } from 'rxjs/operators';
import { NotificationData, NotificationService } from '../../services/notification.service';
import {
  getLicensePlateNumberFromNotification as getLicensePlateNumberFromNotificationUtil,
  getEventDisplayName as getEventDisplayNameUtil,
  getFormattedNotificationTimestamp,
  getNotificationTimeOnly,
  groupNotificationsByDay,
  NotificationDayGroup,
  NotificationRow,
  openNotificationInModal,
} from '../../shared/utils/notification-display.util';
import { ActiveSubscriptionGateService } from '../../services/active-subscription-gate.service';
import {
  dedupeNotificationListItems,
  fetchLprEntries,
  headerDropdownNotificationsCache,
  isLprEntryUpdatedPusherNotification,
  isNotificationListItemUnread,
  markNotificationListItemAsRead,
  mergeKnownReadStateIntoNotificationList,
  NotificationUtils,
  shouldApplyLprEntryUpdateToList,
  upsertNotificationIntoList,
} from '../../notifications/notification.utils';
import { ModalStateService } from '../../services/modal-state.service';
import { WayPurchaseModalStateService } from '../way-purchase-modal/services/way-purchase-modal-state.service';
import { MultiEligibleSelectionModalStateService } from '../multi-eligible-selection-modal/services/multi-eligible-selection-modal-state.service';
import { ApiService } from '../../services/api.service';
import { RoleService } from '../../services/role.service';
import { LocationService } from '../../services/location.service';
import { getUserDataLaneCameraRoi, getUserDataLaneId } from '../../shared/utils/auth-headers.util';

/**
 * Notification dropdown component that loads, displays and manages user notifications from multiple locations.
 * @component
 * @selector app-notification-dropdown
 * @example
 *   <app-notification-dropdown [isOpen]="true"></app-notification-dropdown>
 * @input boolean isOpen - Whether the dropdown is currently open. When set to true the component will load notifications (on init or on change).
 * @output closeRequested void - Emitted when the dropdown should close (View All, item open, Escape).
 *
 * Purpose:
 *   Provides a dropdown UI for viewing notifications aggregated from multiple locations. It handles fetching notifications from the API, deduplicating/formatting them, sorting by timestamp, and marking them as viewed so other parts of the app (e.g. header badge) can update.
 *
 * Key public properties:
 *   - notifications: Array<{ event: string; data: NotificationData; timestamp: string }> - the list shown in the dropdown.
 *   - notificationCount: number - current count of loaded notifications.
 *   - isLoading: boolean - flag used by the template to show/hide a loading indicator.
 *
 * Important static/instance flags:
 *   - static isAnyInstanceLoading - prevents multiple instances from issuing duplicate API requests simultaneously.
 *   - isLoadingNotifications - per-instance guard against concurrent loads.
 *
 * Key methods:
 *   - loadNotifications(): void
 *       Loads notifications from the API for all locations of the current user, handles errors, sorts results by timestamp, updates notificationCount, and calls markNotificationsAsViewed().
 *   - getLocationIds(): Observable<string[]>
 *       Wrapper around LocationService.getLocationIdsForCurrentUser() to obtain location IDs for requests.
 *   - transformApiEntryToNotification(entry): { event, data, timestamp } | null
 *       Maps API entries to the component's notification shape using NotificationUtils.
 *   - getMessageForEventType(eventType): string
 *       Uses NotificationUtils to obtain a user-facing message for an event type.
 *   - subscribeToPusherNotifications(): void
 *       Subscribes to the NotificationService observable to trigger reloads on incoming push events.
 *   - getLicensePlateFromNotification(data): string
 *       Utility wrapper to extract the license plate from notification data.
 *   - getEventDisplayName(event): string
 *       Formats the event name for display.
 *   - getNotificationMessage(message): string
 *       Cleans and maps certain raw messages to friendlier text (e.g. "Invalid license plate detected" -> "Vehicle data not found").
 *   - getFormattedTimestamp(timestamp): string
 *       Formats timestamps for display using a shared util.
 *   - onNotificationClick(notification): void
 *       Opens a notification in a modal via ModalStateService.
 *   - onViewAllClick(event): void
 *       Prevents default and navigates to the notifications page.
 *   - markNotificationsAsViewed(): void
 *       Stores the current timestamp in localStorage and dispatches a custom window event to notify other components that notifications were viewed.
 *
 * Lifecycle hooks:
 *   - ngOnInit(): subscribes to real-time notifications and conditionally loads notifications if created open.
 *   - ngOnChanges(changes: SimpleChanges): reloads notifications when the isOpen input toggles from closed to open (except the initial creation handled in ngOnInit).
 *   - ngOnDestroy(): unsubscribes from push notifications and resets static loading flags if needed to avoid blocking other instances.
 */
@Component({
  selector: 'app-notification-dropdown',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './notification-dropdown.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './notification-dropdown.component.scss',
})
export class NotificationDropdownComponent implements OnInit, OnDestroy, OnChanges {
  @Input() isOpen = false;
  /** Ask parent header to close the dropdown panel. */
  @Output() closeRequested = new EventEmitter<void>();

  private _notifications: NotificationRow[] = [];
  /**
   * Notifications grouped by calendar day (newest day first) for the template.
   * Recomputed whenever the `notifications` list is assigned so it is not rebuilt
   * on every change-detection cycle.
   */
  notificationGroups: NotificationDayGroup[] = [];
  notificationCount = 0;
  isLoading = false; // Public property for template to show loader
  private notificationSubscription: any;
  private lprEntryUpdatedSubscription: any;
  private isLoadingNotifications = false;
  private static isAnyInstanceLoading = false; // Static flag to prevent duplicate loading across all instances
  // Tracks whether the initial load for this open session has been requested, to
  // prevent a second load from the requestAnimationFrame racing with the fast
  // synchronous BehaviorSubject replay in subscribeToPusherNotifications().
  private openSessionLoadRequested = false;
  // Set when a lane change arrives while a load is in flight so we can reload
  // once the current request finishes (otherwise the loading guard drops the refresh).
  private pendingLaneReload = false;
  private selectedLaneChangedHandler: (() => void) | null = null;
  private static locationIdsCache: { ids: string[]; at: number } | null = null;
  private static readonly LOCATION_IDS_CACHE_MS = 60_000;
  /** First page for header bell dropdown — matches `?page=1&limit=10`. */
  private static readonly DROPDOWN_LPR_ENTRIES_PAGE = 0;
  private static readonly DROPDOWN_LPR_ENTRIES_LIMIT = 10;

  constructor(
    private modalStateService: ModalStateService,
    private wayPurchaseModalState: WayPurchaseModalStateService,
    private multiEligibleModalState: MultiEligibleSelectionModalStateService,
    private apiService: ApiService,
    private roleService: RoleService,
    private locationService: LocationService,
    private notificationService: NotificationService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private messageService: NotificationService,
    private activeSubscriptionGateService: ActiveSubscriptionGateService
  ) {}

  /**
   * The notifications shown in the dropdown. Assigning rebuilds the day-grouped
   * view model (`notificationGroups`) so the template groups/separators stay in sync
   * without recomputing on every change-detection cycle.
   */
  get notifications(): NotificationRow[] {
    return this._notifications;
  }
  set notifications(list: NotificationRow[]) {
    this._notifications = list ?? [];
    this.notificationGroups = groupNotificationsByDay(this._notifications);
  }

  /**
   * Initialize component: subscribe to Pusher notifications and, if the dropdown is already open, schedule immediate notification load after render.
   * @example
   * gOnInit()
   * // returns void
   * @returns {void} Initializes subscriptions and triggers notification loading when appropriate.
   **/
  ngOnInit(): void {
    this.openSessionLoadRequested = false;
    // Listen for pusher notifications to reload from API.
    // NOTE: notification$ is a BehaviorSubject — subscribeToPusherNotifications() may
    // synchronously trigger loadNotifications() here via the replayed last value.
    this.subscribeToPusherNotifications();
    this.subscribeToLprEntryUpdates();

    if (typeof window !== 'undefined') {
      this.selectedLaneChangedHandler = () => {
        headerDropdownNotificationsCache.invalidate();
        if (!this.isOpen) {
          return;
        }
        // If a load is already in flight it was configured with the previous lane,
        // so queue a fresh reload to run once it settles instead of dropping it.
        if (this.isLoadingNotifications) {
          this.pendingLaneReload = true;
          return;
        }
        this.loadNotifications(false, false);
      };
      window.addEventListener('way-selected-lane-changed', this.selectedLaneChangedHandler);
    }

    // If component is created with isOpen=true (from *ngIf), load notifications immediately.
    // Only schedule the rAF if subscribeToPusherNotifications() has NOT already requested a
    // load (which it does when the BehaviorSubject had a non-null cached value). This prevents
    // a double-load race that causes the loader to flash twice on fast connections.
    if (this.isOpen && !this.openSessionLoadRequested) {
      requestAnimationFrame(() => {
        if (this.isOpen && !this.openSessionLoadRequested) {
          this.loadNotifications(false, false);
        }
      });
    }
  }

  /**
   * Unsubscribes from notification stream and clears the component loading flag when destroyed.
   * @example
   * gOnDestroy()
   * undefined
   * @returns {void} Performs cleanup (unsubscribes and resets static loading flag) and returns nothing.
   **/
  ngOnDestroy(): void {
    if (this.notificationSubscription) {
      this.notificationSubscription.unsubscribe();
    }
    if (this.lprEntryUpdatedSubscription) {
      this.lprEntryUpdatedSubscription.unsubscribe();
    }
    if (typeof window !== 'undefined' && this.selectedLaneChangedHandler) {
      window.removeEventListener('way-selected-lane-changed', this.selectedLaneChangedHandler);
      this.selectedLaneChangedHandler = null;
    }

    // Reset static loading flag if this instance was loading
    // This prevents blocking new instances when component is destroyed rapidly
    if (this.isLoadingNotifications) {
      NotificationDropdownComponent.isAnyInstanceLoading = false;
    }
  }

  /**
   * Handle input changes; when isOpen transitions from false to true (excluding the initial change), schedule loading notifications.
   * @example
   * gOnChanges({ isOpen: { currentValue: true, previousValue: false, firstChange: false } })
   * undefined
   * @param {{SimpleChanges}} {{changes}} - Object mapping input names to SimpleChange objects describing current/previous values and firstChange.
   * @returns {{void}} No return value.
   **/
  ngOnChanges(changes: SimpleChanges): void {
    // Reload notifications when dropdown opens
    // This handles when dropdown is toggled open (not initial creation, which is handled in ngOnInit)
    if (changes['isOpen']) {
      const isCurrentlyOpen = changes['isOpen'].currentValue === true;
      const wasPreviouslyOpen = changes['isOpen'].previousValue === true;
      const isFirstChange = changes['isOpen'].firstChange;

      // Load notifications when opening from closed state (not on first change, as ngOnInit handles that)
      if (isCurrentlyOpen && !wasPreviouslyOpen && !isFirstChange) {
        // Use requestAnimationFrame for better reliability
        requestAnimationFrame(() => {
          // Double-check component is still open before loading
          if (this.isOpen) {
            this.loadNotifications(false, false);
          }
        });
      }
    }
  }

  /**
   * Load notifications from API.
   * @param silent When true the loader spinner is suppressed so existing list stays visible
   *               during background refreshes (e.g. incoming Pusher events while open).
   * @param forceRefresh When false, reuse header cache if still fresh (avoids duplicate API after Pusher).
   */
  loadNotifications(silent = false, forceRefresh = false): void {
    // Don't load if component is not open
    if (!this.isOpen) {
      return;
    }

    if (!forceRefresh && !silent && headerDropdownNotificationsCache.isFresh()) {
      this.applyCachedNotifications();
      return;
    }

    // Prevent multiple simultaneous API calls from this instance
    if (this.isLoadingNotifications) {
      return;
    }

    // If another instance is loading, wait a short time and retry (handles rapid destroy/create)
    if (NotificationDropdownComponent.isAnyInstanceLoading) {
      setTimeout(() => {
        if (this.isOpen && !this.isLoadingNotifications) {
          this.loadNotifications(silent, forceRefresh);
        }
      }, 50);
      return;
    }

    // Mark that a load has been requested for this open session so ngOnInit's
    // requestAnimationFrame callback can skip a redundant second load.
    this.openSessionLoadRequested = true;

    this.isLoadingNotifications = true;
    NotificationDropdownComponent.isAnyInstanceLoading = true;
    // Only show the spinner on the initial/explicit load; background Pusher-triggered
    // refreshes use silent=true to avoid the list flickering back to a loader.
    if (!silent) {
      this.isLoading = true;
    }

    this.getLocationIds().subscribe({
      next: (locationIds: string[]) => {
        if (locationIds.length === 0) {
          console.warn('No location IDs available');
          this.notifications = [];
          this.notificationCount = 0;
          this.isLoadingNotifications = false;
          NotificationDropdownComponent.isAnyInstanceLoading = false;
          this.isLoading = false;
          this.consumePendingLaneReload();
          return;
        }

        // Fetch each location independently so the list renders as soon as the first
        // response arrives (forkJoin waits for the slowest location).
        const aggregated: typeof this.notifications = [];
        if (!silent) {
          this.notifications = [];
          this.notificationCount = 0;
        }

        let pending = locationIds.length;

        const applyAggregatedToView = (): void => {
          const merged = dedupeNotificationListItems(aggregated).slice(
            0,
            NotificationDropdownComponent.DROPDOWN_LPR_ENTRIES_LIMIT
          );
          const withReadState = mergeKnownReadStateIntoNotificationList(
            merged,
            this._notifications,
            headerDropdownNotificationsCache.items
          );
          this.notifications = withReadState;
          this.notificationCount = withReadState.length;
        };

        const onLocationSettled = (): void => {
          if (!silent) {
            applyAggregatedToView();
          }
          // Don't clear the loader on an empty response — it flashes "No activity" while slower locations are still in flight.
          if (this.notificationCount > 0) {
            this.isLoading = false;
          }
          pending--;
          if (pending === 0) {
            applyAggregatedToView();
            headerDropdownNotificationsCache.set(this.notifications);
            this.isLoadingNotifications = false;
            NotificationDropdownComponent.isAnyInstanceLoading = false;
            this.isLoading = false;
            this.cdr.markForCheck();
            this.consumePendingLaneReload();
          } else {
            this.cdr.markForCheck();
          }
        };

        locationIds.forEach(locationId => {
          fetchLprEntries(this.apiService, locationId, {
            page: NotificationDropdownComponent.DROPDOWN_LPR_ENTRIES_PAGE,
            limit: NotificationDropdownComponent.DROPDOWN_LPR_ENTRIES_LIMIT,
            laneId: getUserDataLaneId() ?? null,
          })
            .pipe(
              catchError(error => {
                console.error(`Error fetching notifications for location ${locationId}:`, error);
                return of({ newNotifications: [], paginationData: null });
              })
            )
            .subscribe({
              next: ({ newNotifications }) => {
                if (newNotifications.length > 0) {
                  aggregated.push(...newNotifications);
                }
                onLocationSettled();
              },
              error: () => onLocationSettled(),
            });
        });
      },
      error: (error: any) => {
        console.error('Error getting location IDs:', error);
        if (this.isOpen) {
          this.notifications = [];
          this.notificationCount = 0;
        }
        this.isLoadingNotifications = false;
        NotificationDropdownComponent.isAnyInstanceLoading = false;
        this.isLoading = false;
        this.consumePendingLaneReload();
      },
    });
  }

  /**
   * If a lane change arrived while a load was in flight, run the queued reload now
   * that the current request has finished so the rendered list matches the latest lane.
   */
  private consumePendingLaneReload(): void {
    if (!this.pendingLaneReload) {
      return;
    }
    this.pendingLaneReload = false;
    if (this.isOpen) {
      // Defer so the current call stack unwinds before starting the next load.
      setTimeout(() => {
        if (this.isOpen && !this.isLoadingNotifications) {
          this.loadNotifications(false, true);
        }
      }, 0);
    }
  }

  private getLocationIds(): any {
    const now = Date.now();
    const cached = NotificationDropdownComponent.locationIdsCache;
    if (cached && now - cached.at < NotificationDropdownComponent.LOCATION_IDS_CACHE_MS) {
      return of(cached.ids);
    }
    return this.locationService.getLocationIdsForCurrentUser().pipe(
      tap((ids: string[]) => {
        NotificationDropdownComponent.locationIdsCache = { ids, at: Date.now() };
      })
    );
  }

  private sortNotificationsByTimestamp(): void {
    const sorted = [...this._notifications].sort((a, b) => {
      const dateA = new Date(a.timestamp).getTime();
      const dateB = new Date(b.timestamp).getTime();
      return dateB - dateA;
    });
    // Reassign through the setter so the grouped view model is rebuilt in sorted order.
    this.notifications = sorted;
  }

  private transformApiEntryToNotification(
    entry: any
  ): { event: string; data: NotificationData; timestamp: string } | null {
    return NotificationUtils.transformApiEntryToNotification(entry);
  }

  private getMessageForEventType(eventType: NotificationData['type']): string {
    return NotificationUtils.getMessageForEventType(eventType);
  }

  /**
   * Subscribe to pusher notifications to reload from API.
   * notification$ is a BehaviorSubject so this fires synchronously on subscribe
   * if a non-null value was previously emitted (replayed last value). When that
   * happens we treat it as the initial load (silent=false). For subsequent real
   * Pusher events that arrive while the dropdown is already showing data, we use
   * silent=true so the existing list stays visible instead of flickering to a loader.
   */
  private subscribeToPusherNotifications(): void {
    this.notificationSubscription = this.notificationService.notification$
      .pipe(filter((n): n is NotificationData => n != null))
      .subscribe(notification => {
        if (isLprEntryUpdatedPusherNotification(notification)) {
          this.applyInPlaceLprEntryUpdate(notification);
          return;
        }
        if (!this.isOpen) {
          return;
        }
        if (headerDropdownNotificationsCache.isFresh()) {
          this.applyCachedNotifications();
          return;
        }
        const silent = this.notifications.length > 0;
        this.loadNotifications(silent, false);
      });
  }

  /** Reuse list already fetched for header badge — no API call. */
  private applyCachedNotifications(): void {
    this.openSessionLoadRequested = true;
    this.notifications = headerDropdownNotificationsCache.getTop(
      NotificationDropdownComponent.DROPDOWN_LPR_ENTRIES_LIMIT
    );
    this.notificationCount = this.notifications.length;
    this.isLoading = false;
    this.isLoadingNotifications = false;
    NotificationDropdownComponent.isAnyInstanceLoading = false;
    this.cdr.markForCheck();
  }

  /** Silent in-place patch for LPR correction events — no API reload. */
  private subscribeToLprEntryUpdates(): void {
    this.lprEntryUpdatedSubscription =
      this.notificationService.lprEntryUpdatedNotification$.subscribe({
        next: value => {
          if (!value) return;
          const { notification } = value;
          this.applyInPlaceLprEntryUpdate(notification);
        },
      });
  }

  /** Patch the matching row only — no loader, no full API reload. */
  private applyInPlaceLprEntryUpdate(notification: NotificationData): void {
    if (
      !shouldApplyLprEntryUpdateToList(
        [this.notifications, headerDropdownNotificationsCache.items],
        notification,
        getUserDataLaneId(),
        getUserDataLaneCameraRoi()
      )
    ) {
      return;
    }
    headerDropdownNotificationsCache.upsertFromNotification(notification);
    this.notifications = upsertNotificationIntoList(this.notifications, notification);
    this.sortNotificationsByTimestamp();
    this.notificationCount = this.notifications.length;
    this.cdr.markForCheck();
  }

  /**
   * Plate number for display (state prefix stripped). Identity/matching elsewhere
   * still uses the full state+number value.
   */
  getLicensePlateFromNotification(data: NotificationData): string {
    return getLicensePlateNumberFromNotificationUtil(data);
  }

  /**
   * Get formatted event name for display
   */
  getEventDisplayName(
    event: string,
    data?: { event: string; data: NotificationData; timestamp: string }['data']
  ): string {
    return getEventDisplayNameUtil(event, data);
  }

  /**
   * Get formatted notification message for display
   */
  getNotificationMessage(message: string | undefined): string {
    if (!message) {
      return 'No message';
    }

    // Replace "Invalid License Plate" with "Vehicle data not found"
    if (message.includes('Invalid license plate detected')) {
      return 'Vehicle data not found';
    }
    if (message.includes('Customer request created')) {
      return 'New customer order request';
    }

    return message;
  }

  /**
   * Get formatted timestamp for display with date and time in "month date year, time" format
   */
  getFormattedTimestamp(timestamp: string): string {
    return getFormattedNotificationTimestamp(timestamp);
  }

  /**
   * Time-only label (e.g. "6:13 PM") shown on the right of each row; the date lives
   * in the per-day separator header instead.
   */
  getNotificationTime(timestamp: string): string {
    return getNotificationTimeOnly(timestamp);
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.isOpen) {
      this.requestClose();
    }
  }

  requestClose(): void {
    this.closeRequested.emit();
  }

  /**
   * Handle notification item click — mark that row read and open the notification modal.
   * The dropdown stays open so read/unread styling remains visible in the list.
   */
  onNotificationClick(notification: NotificationRow): void {
    if (this.roleService.isStaff()) {
      return;
    }
    this.markNotificationAsRead(notification);
    openNotificationInModal(
      notification,
      this.modalStateService,
      this.wayPurchaseModalState,
      this.messageService,
      this.activeSubscriptionGateService,
      this.multiEligibleModalState
    );
  }

  /** True when the row is still unread (backed by LPR `is_read` from the API). */
  isNotificationUnread(notification: NotificationRow): boolean {
    return isNotificationListItemUnread(notification);
  }

  /** Mark one notification as read via the shared LPR entries API helper. */
  private markNotificationAsRead(notification: NotificationRow): void {
    markNotificationListItemAsRead(
      this.apiService,
      notification,
      () => this.notifications,
      list => {
        this.notifications = list;
        headerDropdownNotificationsCache.set(list);
      },
      { onListUpdated: () => this.cdr.markForCheck() }
    );
  }

  /**
   * Handle View All click - close dropdown and navigate to notifications page
   */
  onViewAllClick(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.roleService.isStaff()) {
      return;
    }
    this.requestClose();
    this.router.navigate(['/notifications']);
  }

  trackByGroup(_: number, group: NotificationDayGroup): string {
    return group.key;
  }
  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
